import React, { useContext, useState, useEffect } from "react";
import AuthButton from "../Application/AuthButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  IconButton,
  Tooltip,
  Popover,
  Box,
  Typography,
  Button,
  Link,
  Chip,
  CircularProgress,
  Badge,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { ColorModeContext } from "../theme/ColorModeProvider";
import ProgressIndicator from "../Application/components/discovery/ProgressIndicator";
import ChannelSwitcher from "../Application/ChannelSwitcher";
import AIAnnouncementModal from "../Application/components/AIAnnouncementModal";
import SmartToyIcon from "@mui/icons-material/SmartToy";



const TopBar = ({
  onAuthSuccess,
  onClearConfiguration,
  onDisconnectFB,
  isConfigured,
  token,
  pages = [],
  selectedPage,
  managedPages = [],
  onPageSelect,
  onAddChannel,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [whatsNewAnchor, setWhatsNewAnchor] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [planName, setPlanName] = useState("");
  const [plans, setPlans] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const navigate = useNavigate();
  const { mode, toggleColorMode } = useContext(ColorModeContext);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!token) return;
      setSubscriptionLoading(true);
      try {
        const [statusRes, plansRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/subscription/status`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/subscription/plans`),
        ]);
        const subscription = statusRes.data.subscription;
        const plansData = plansRes.data.plans || [];
        setPlans(plansData);
        setSubscriptionStatus(subscription);
        if (subscription?.planId) {
          const plan = plansData.find((p) => p._id === subscription.planId);
          setPlanName(plan ? plan.name : subscription.planId || "");
        }
      } catch (err) {
        // Silently fail - subscription info is optional
      } finally {
        setSubscriptionLoading(false);
      }
    };
    if (token && isLoggedIn) {
      fetchSubscriptionStatus();
    }
  }, [token, isLoggedIn]);

  return (
    <div className="flex h-16 justify-between items-center px-5 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-[1000] backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
      <div className="flex items-center gap-2">
        <div
          onClick={() => {
            if (isLoggedIn) {
              navigate("/dashboard");
            } else {
              navigate("/");
            }
          }}
          className="text-xl font-bold text-gray-900 dark:text-white cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          <div className="flex items-center gap-2">
            <img src="/assets/revamp/logos/Reeltale icon.png" alt="ReelTale Icon" className="h-8 w-8 object-contain" />
            <span className="hidden sm:inline-block text-xl font-black tracking-tighter text-gray-900 dark:text-white">ReelTale</span>
          </div>
        </div>
        {isLoggedIn && token && pages.length > 0 && (
          <Box className="ml-4">
            <ChannelSwitcher
              pages={pages}
              selectedPage={selectedPage}
              managedPages={managedPages}
              onPageSelect={onPageSelect}
              onAddChannel={onAddChannel}
              onClearConfiguration={onClearConfiguration}
            />
          </Box>
        )}
        {isLoggedIn && token && (
          <Box className="ml-4 flex items-center">
            <ProgressIndicator />
          </Box>
        )}

        {isLoggedIn && token && (
          <Box className="flex items-center gap-2 ml-4">
            {subscriptionLoading ? (
              <CircularProgress size={16} />
            ) : subscriptionStatus && subscriptionStatus.planId ? (
                  <>
                    <Typography
                      variant="body2"
                      className="text-sm text-gray-600 dark:text-gray-400"
                    >
                      Plan: <strong className="text-gray-900 dark:text-white">{planName || "N/A"}</strong>
                    </Typography>
                    <Chip
                      label={subscriptionStatus.status || "N/A"}
                      color={
                        subscriptionStatus.status === "active"
                          ? "success"
                          : "default"
                      }
                      size="small"
                      sx={{ height: "24px", fontSize: "0.75rem" }}
                    />
                    {(() => {
                      const currentPlan = plans.find(
                        (p) => p._id === subscriptionStatus.planId
                      );
                      const isHighestPlan =
                        plans.length > 0 &&
                        currentPlan &&
                        plans.every((p) => p.price <= currentPlan.price);
                      return !isHighestPlan ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => navigate("/pricing")}
                          className="ml-2 text-xs h-6 px-3 normal-case"
                        >
                          Upgrade
                        </Button>
                      ) : null;
                    })()}
                  </>
                ) : null}
          </Box>
        )}
      </div>
      <div className="flex items-center gap-2">
        <a
          href={isLoggedIn ? "/pricing" : "#pricing"}
          className="no-underline text-gray-600 dark:text-gray-400 text-base cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          onClick={(e) => {
            if (isLoggedIn) {
              e.preventDefault();
              navigate("/pricing");
            } else {
              e.preventDefault();
              document.getElementById("pricing")?.scrollIntoView({
                behavior: "smooth",
              });
            }
          }}
        >
          Pricing
        </a>

        {isLoggedIn && (
          <Tooltip title="What's New">
            <IconButton
              onClick={(e) => setWhatsNewAnchor(e.currentTarget)}
              size="small"
              className="!p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <Badge 
                color="error" 
                variant="dot" 
                overlap="circular"
                sx={{ "& .MuiBadge-badge": { top: 2, right: 2 } }}
              >
                <LightbulbIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        <Popover
          open={Boolean(whatsNewAnchor)}
          anchorEl={whatsNewAnchor}
          onClose={() => setWhatsNewAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            className: "rounded-2xl shadow-soft-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          }}
        >
          <Box className="p-0 max-w-md">
            {/* AI Announcement Header (Highlighted) */}
            <Box className="p-6 bg-gradient-to-br from-indigo-50 to-primary-50 dark:from-indigo-900/10 dark:to-primary-900/10 border-b border-gray-100 dark:border-gray-800">
                <Box className="flex items-center gap-2 mb-3">
                    <SmartToyIcon color="primary" />
                    <Typography variant="h6" className="font-bold text-gray-900 dark:text-white">
                        🧠 New: AI Mood-Aware Comments
                    </Typography>
                </Box>
                <Typography variant="body1" className="mb-4 text-gray-700 dark:text-gray-300">
                    Defend your brand with our new AI. It analyzes every comment to handle "Fake" tags, 
                    shipping queries, and more! <strong>Try it now.</strong>
                </Typography>
                <div className="flex gap-2">
                    <Button 
                        variant="contained" 
                        size="small" 
                        className="btn-primary flex-1 normal-case"
                        onClick={() => {
                            setWhatsNewAnchor(null);
                            // Navigate to dashboard where they can see their posts to configure
                            navigate("/dashboard");
                        }}
                    >
                        Configure your Post
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="small" 
                        className="flex-1 bg-white/50 backdrop-blur-sm"
                        onClick={() => {
                            setWhatsNewAnchor(null);
                            setAiModalOpen(true);
                        }}
                    >
                        How it works
                    </Button>
                </div>
            </Box>

            {/* Existing Updates */}
            <Box className="p-6">
                <Box className="flex items-center gap-2 mb-2 opacity-50">
                <LocalShippingIcon fontSize="small" />
                <Typography variant="subtitle2" className="font-bold text-gray-900 dark:text-white">
                    Built-in Shipping Integration
                </Typography>
                </Box>
                <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-4">
                Generate shipping labels and track packages all in one place.
                </Typography>
                <Button
                variant="text"
                size="small"
                onClick={() => {
                    setWhatsNewAnchor(null);
                    navigate("/dashboard/shipping");
                }}
                startIcon={<LocalShippingIcon />}
                className="text-primary-600 font-bold p-0 min-w-0 hover:bg-transparent normal-case"
                >
                Go to Shipping
                </Button>
            </Box>
          </Box>
        </Popover>
        
        <AIAnnouncementModal 
            open={aiModalOpen} 
            onClose={() => setAiModalOpen(false)} 
            onExplore={() => navigate("/dashboard")}
        />
        <Tooltip title={mode === "dark" ? "Switch to light" : "Switch to dark"}>
          <IconButton
            onClick={toggleColorMode}
            size="small"
            className="!p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {!isLoggedIn ? (
          <>
            <a
              href="/login"
              className="no-underline text-gray-600 dark:text-gray-400 text-base cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Login
            </a>
            <button
              onClick={() => (window.location.href = "/signup")}
              className="btn-primary text-base px-4 py-2"
            >
              Create Free Account
            </button>
          </>
        ) : (
          <>
            {token && (
              <a
                href="#configuration"
                className="no-underline text-gray-600 dark:text-gray-400 text-base cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  if (onDisconnectFB) {
                    onDisconnectFB();
                  }
                }}
              >
                Disconnect FB
              </a>
            )}
            <button
              onClick={() => {
                localStorage.removeItem("token");
                setIsLoggedIn(false);
                window.location.href = "/";
              }}
              className="btn-primary text-base px-4 py-2"
            >
              Logout
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TopBar;
